import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { batchedIn } from "@/lib/batchedQuery";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Heart,
  Baby,
  Ambulance,
  Activity,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  DollarSign,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Gauge,
  Building2
} from "lucide-react";
import { BarChart, Bar, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface ScenarioProcedure {
  code: string;
  code_type: string;
  description: string;
  phase: string;
  quantity: number;
  estimated_cost: number;
}

interface ScenarioWithCost {
  id: string;
  name: string;
  description: string;
  icon_name: string;
  color: string;
  duration: string;
  total_procedures: number;
  phases: string[];
  is_active: boolean;
  created_at: string;
  calculated_avg_cost: number;
  calculated_min_cost: number;
  calculated_max_cost: number;
  codeTypeBreakdown?: Record<string, { cost: number; count: number }>;
  phaseBreakdown?: Record<string, number>;
  phaseProcedures?: Record<string, ScenarioProcedure[]>;
  severity_level?: number;
  tags?: string[];
}

const iconMap: Record<string, any> = {
  Heart,
  Baby,
  Ambulance,
  Activity,
};

// Color palette for phases (using a gradient from start to end of treatment)
const PHASE_COLORS = [
  '#22c55e', // Green - Start/Diagnosis
  '#3b82f6', // Blue - Early treatment
  '#8b5cf6', // Purple - Mid treatment
  '#ec4899', // Pink - Late treatment
  '#f59e0b', // Amber - Recovery
  '#ef4444', // Red - Follow-up
];

// Custom tooltip for the chart
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-background/95 border border-matrix-green/30 p-2 rounded shadow-lg">
        <p className="text-xs font-semibold text-matrix-green">{data.name}</p>
        <p className="text-sm font-mono text-blood-red">
          ${data.cost.toLocaleString()}
        </p>
        <p className="text-xs text-muted-foreground">
          {((data.cost / data.totalCost) * 100).toFixed(1)}% of total cost
        </p>
      </div>
    );
  }
  return null;
};

export function ScenariosPreview() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 4; // Show 4 scenarios per page
  const [searchQuery, setSearchQuery] = useState('');
  const [showAutocompleteDropdown, setShowAutocompleteDropdown] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch scenarios with their real costs from materialized view
  const { data: scenarios = [], isLoading } = useQuery({
    queryKey: ['scenarios-preview-with-real-costs-v1'],
    queryFn: async () => {
      // Fetch all active scenarios
      const { data: scenariosData, error: scenariosError } = await supabase
        .from('scenarios')
        .select('*')
        .eq('is_active', true)
        .eq('hidden_from_catalog', false)
        .order('created_at', { ascending: true })
        .limit(20);

      if (scenariosError) {
        console.error('Error fetching scenarios:', scenariosError);
        throw scenariosError;
      }

      // Fetch real costs and procedures (batched to avoid URL length limits)
      const scenarioIds = scenariosData.map(s => s.id);
      const [{ data: realCostsData, error: costsError }, { data: allProcedures, error: procError }] = await Promise.all([
        batchedIn('scenario_costs_cache', 'scenario_id', scenarioIds),
        batchedIn(
          'scenario_procedures',
          'scenario_id',
          scenarioIds,
          'scenario_id, code, code_type, estimated_cost, quantity, phase'
        ),
      ]);

      if (costsError) {
        console.error('Error fetching real costs:', costsError);
      }

      // Create maps for real costs
      const realCostsMap = new Map(
        realCostsData?.map(rc => [rc.scenario_id, {
          total: rc.real_total_cost || 0,
          min: rc.real_min_cost || 0,
          max: rc.real_max_cost || 0,
          phaseCosts: rc.phase_costs || {},
        }]) || []
      );

      if (procError) {
        console.error('Error fetching procedures:', procError);
      }

      // Calculate costs using REAL pricing from materialized view
      const scenariosWithCosts = scenariosData.map(scenario => {
        const procedures = allProcedures?.filter(p => p.scenario_id === scenario.id) || [];

        // Get real costs from cache (including real phase costs)
        const realCosts = realCostsMap.get(scenario.id) || { total: 0, min: 0, max: 0, phaseCosts: {} };
        const realTotalCost = realCosts.total;

        // Group procedures for display (but don't calculate costs - use real costs from cache)
        const codeTypeBreakdown: Record<string, { cost: number; count: number }> = {};
        const phaseProcedures: Record<string, ScenarioProcedure[]> = {};

        procedures.forEach(proc => {
          // Track by code type (count only, costs come from cache)
          const codeType = proc.code_type.toUpperCase();
          if (!codeTypeBreakdown[codeType]) {
            codeTypeBreakdown[codeType] = { cost: 0, count: 0 };
          }
          codeTypeBreakdown[codeType].count += 1;

          // Organize procedures by phase for display
          const phase = proc.phase || 'Other';
          if (!phaseProcedures[phase]) {
            phaseProcedures[phase] = [];
          }
          phaseProcedures[phase].push({
            code: proc.code,
            code_type: proc.code_type,
            description: proc.description || '',
            phase: proc.phase,
            quantity: proc.quantity || 1,
            estimated_cost: Number(proc.estimated_cost || 0),
          });
        });

        // Use REAL phase costs from cache (no scaling/estimation)
        const realPhaseBreakdown = realCosts.phaseCosts || {};

        return {
          ...scenario,
          calculated_avg_cost: Math.round(realTotalCost),
          calculated_min_cost: Math.round(realCosts.min),
          calculated_max_cost: Math.round(realCosts.max),
          codeTypeBreakdown,
          phaseBreakdown: realPhaseBreakdown,
          phaseProcedures,
        } as ScenarioWithCost;
      });

      return scenariosWithCosts;
    },
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
  });

  // Filter scenarios for autocomplete dropdown only - doesn't affect displayed scenarios
  const autocompleteScenarios = scenarios.filter(s => {
    const searchLower = searchQuery.toLowerCase();
    return (
      s.name.toLowerCase().includes(searchLower) ||
      s.description.toLowerCase().includes(searchLower) ||
      s.phases.some(p => p.toLowerCase().includes(searchLower)) ||
      (s.tags && s.tags.some(t => t.toLowerCase().includes(searchLower)))
    );
  });

  // Always show first 4 scenarios from the full list (unfiltered)
  const totalPages = Math.ceil(scenarios.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const currentScenarios = scenarios.slice(startIndex, startIndex + itemsPerPage);

  // Close autocomplete dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setShowAutocompleteDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || Activity;
  };

  const createSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Get complexity color based on severity level (1-10)
  const getComplexityColor = (level?: number) => {
    if (!level) return 'text-muted-foreground';
    if (level <= 3) return 'text-green-500';
    if (level <= 6) return 'text-yellow-500';
    if (level <= 8) return 'text-orange-500';
    return 'text-red-500';
  };

  // Get complexity label
  const getComplexityLabel = (level?: number) => {
    if (!level) return 'Unknown';
    if (level <= 3) return 'Simple';
    if (level <= 6) return 'Moderate';
    if (level <= 8) return 'Complex';
    return 'Very Complex';
  };

  // Toggle phase expansion
  const togglePhase = (scenarioId: string, phase: string) => {
    const key = `${scenarioId}-${phase}`;
    setExpandedPhases(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (isLoading) {
    return (
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <p className="text-matrix-green font-mono">Calculating real-time pricing from hospital data...</p>
          </div>
        </div>
      </section>
    );
  }

  if (scenarios.length === 0) {
    return null;
  }

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 z-10 bg-secondary/20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-matrix-green mb-3 sm:mb-4 glitch" data-text="Common Medical Scenarios">
            Common Medical Scenarios
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-4">
            Real pricing calculated from {' '}
            <span className="text-matrix-green font-mono">actual hospital data</span>
          </p>
          <Link to="/scenarios">
            <Button variant="outline" className="border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10">
              View All Scenarios
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Search Input with Autocomplete */}
        <div className="mb-6 max-w-xl mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search scenarios..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowAutocompleteDropdown(true);
              }}
              onFocus={() => setShowAutocompleteDropdown(true)}
              className="w-full bg-black/60 border-matrix-green/20 font-mono text-sm pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setShowAutocompleteDropdown(true);
                  searchInputRef.current?.focus();
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-matrix-green transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Autocomplete Dropdown */}
            {showAutocompleteDropdown && (searchQuery === '' || autocompleteScenarios.length > 0) && (
              <div
                ref={dropdownRef}
                className="absolute z-[9999] w-full mt-1 bg-black/95 border border-matrix-green/20 rounded-lg shadow-lg max-h-[400px] overflow-y-auto thin-scrollbar"
              >
                {(searchQuery === '' ? scenarios : autocompleteScenarios).slice(0, 10).map((scenario) => {
                  const IconComponent = getIcon(scenario.icon_name);
                  const slug = createSlug(scenario.name);

                  return (
                    <button
                      key={scenario.id}
                      onClick={() => {
                        navigate(`/scenarios/${scenario.id}/${slug}`);
                        setSearchQuery('');
                        setShowAutocompleteDropdown(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-matrix-green/10 transition-colors border-b border-matrix-green/10 last:border-0"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="p-2 rounded flex-shrink-0"
                          style={{ backgroundColor: `${scenario.color}20` }}
                        >
                          <IconComponent
                            className="w-5 h-5"
                            style={{ color: scenario.color }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm mb-1 truncate">{scenario.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{scenario.description}</div>
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-xs text-blood-red font-mono">
                              ${scenario.calculated_avg_cost > 0 ? scenario.calculated_avg_cost.toLocaleString() : 'N/A'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {scenario.total_procedures} procedures
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {scenario.duration}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* No Results */}
            {showAutocompleteDropdown && searchQuery && autocompleteScenarios.length === 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-[9999] w-full mt-1 bg-black/95 border border-matrix-green/20 rounded-lg shadow-lg p-4"
              >
                <div className="text-center text-sm text-muted-foreground">
                  No scenarios found matching "{searchQuery}"
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Scenarios List - Full Width */}
        <div className="flex flex-col gap-6 mb-8">
          {currentScenarios.map((scenario) => {
            const IconComponent = getIcon(scenario.icon_name);
            const slug = createSlug(scenario.name);

            // Prepare chart data from phase breakdown (ordered by scenario phases for timeline)
            const phaseOrder = scenario.phases || [];
            const chartData = phaseOrder
              .map((phase, index) => ({
                name: phase,
                cost: scenario.phaseBreakdown?.[phase] || 0,
                totalCost: scenario.calculated_avg_cost,
                color: PHASE_COLORS[index % PHASE_COLORS.length],
              }))
              .filter(item => item.cost > 0);

            return (
              <Card key={scenario.id} className="terminal-card hover:border-matrix-green/50 transition-all">
                <div className="p-6 space-y-4">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className="p-3 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${scenario.color}20` }}
                      >
                        <IconComponent
                          className="w-8 h-8"
                          style={{ color: scenario.color }}
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">{scenario.name}</h3>
                        <p className="text-sm text-muted-foreground">{scenario.description || 'Medical scenario cost breakdown'}</p>
                      </div>
                    </div>

                    {/* Complexity Score */}
                    {scenario.severity_level && (
                      <div className="flex flex-col items-end">
                        <div className="flex items-center gap-2 mb-1">
                          <Gauge className={`w-4 h-4 ${getComplexityColor(scenario.severity_level)}`} />
                          <span className="text-xs text-muted-foreground">Complexity</span>
                        </div>
                        <div className={`text-sm font-bold ${getComplexityColor(scenario.severity_level)}`}>
                          {getComplexityLabel(scenario.severity_level)} ({scenario.severity_level}/10)
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Main Content Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                    {/* Left Side - Stats */}
                    <div className="md:col-span-2 space-y-4">
                      {/* Cost */}
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Total Estimated Cost</p>
                        <p className="text-2xl font-bold text-blood-red flex items-center">
                          <DollarSign className="w-5 h-5" />
                          {scenario.calculated_avg_cost > 0 ? scenario.calculated_avg_cost.toLocaleString() : 'N/A'}
                        </p>
                      </div>

                      {/* Duration & Procedures */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Duration</p>
                          <p className="text-sm font-mono text-foreground">{scenario.duration}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Procedures</p>
                          <p className="text-sm font-mono text-matrix-green">{scenario.total_procedures}</p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-2">
                        <Link to={`/scenarios/${scenario.id}/${slug}`}>
                          <Button className="w-full bg-matrix-green/10 hover:bg-matrix-green/20 text-matrix-green border border-matrix-green/30">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Explore Full Details
                          </Button>
                        </Link>
                        <Link to={`/scenarios/${scenario.id}/${slug}?focus=hospital`}>
                          <Button variant="outline" className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
                            <Building2 className="w-4 h-4 mr-2" />
                            Compare with Hospital
                          </Button>
                        </Link>
                      </div>
                    </div>

                    {/* Right Side - Chart */}
                    {chartData.length > 0 && (
                      <div className="md:col-span-3">
                        <p className="text-sm font-semibold text-muted-foreground mb-3">Cost Timeline by Treatment Phase</p>
                        <ResponsiveContainer width="100%" height={Math.max(chartData.length * 40, 200)}>
                          <BarChart
                            data={chartData}
                            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                            layout="vertical"
                            barCategoryGap="15%"
                          >
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={150}
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <XAxis type="number" hide />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(34, 197, 94, 0.1)' }} />
                            <Bar dataKey="cost" radius={[0, 6, 6, 0]} barSize={24}>
                              {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  {/* Expandable Phase Sections */}
                  {scenario.phaseProcedures && Object.keys(scenario.phaseProcedures).length > 0 && (
                    <div className="mt-4 border-t border-matrix-green/20 pt-4">
                      <p className="text-xs font-semibold text-muted-foreground mb-3">PROCEDURE BREAKDOWN BY PHASE</p>
                      <div className="space-y-2">
                        {scenario.phases.map((phase, idx) => {
                          const phaseCodes = scenario.phaseProcedures?.[phase] || [];
                          const phaseCost = scenario.phaseBreakdown?.[phase] || 0;
                          const isExpanded = expandedPhases[`${scenario.id}-${phase}`];
                          const phaseColor = PHASE_COLORS[idx % PHASE_COLORS.length];

                          return (
                            <div key={phase} className="border border-matrix-green/10 rounded-lg overflow-hidden">
                              <button
                                onClick={() => togglePhase(scenario.id, phase)}
                                className="w-full px-4 py-3 flex items-center justify-between bg-black/30 hover:bg-black/50 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: phaseColor }}
                                  />
                                  <span className="text-sm font-semibold">{phase}</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {phaseCodes.length} code{phaseCodes.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-mono text-blood-red">
                                    ${phaseCost.toLocaleString()}
                                  </span>
                                  {isExpanded ? (
                                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </div>
                              </button>

                              {isExpanded && (
                                <div className="px-4 py-3 bg-black/20 space-y-2">
                                  {phaseCodes.map((proc, procIdx) => (
                                    <div
                                      key={procIdx}
                                      className="flex items-start justify-between gap-4 p-2 rounded hover:bg-matrix-green/5 transition-colors"
                                    >
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge variant="outline" className="text-xs font-mono">
                                            {proc.code_type}: {proc.code}
                                          </Badge>
                                          {proc.quantity > 1 && (
                                            <Badge variant="secondary" className="text-xs">
                                              ×{proc.quantity}
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">{proc.description}</p>
                                      </div>
                                      <div className="text-sm font-mono text-blood-red whitespace-nowrap">
                                        ${(proc.estimated_cost * proc.quantity).toLocaleString()}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={prevPage}
              disabled={currentPage === 0}
              className="border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentPage
                      ? 'bg-matrix-green w-8'
                      : 'bg-matrix-green/30 hover:bg-matrix-green/50'
                  }`}
                  aria-label={`Go to page ${i + 1}`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={nextPage}
              disabled={currentPage === totalPages - 1}
              className="border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Page Indicator */}
        {totalPages > 1 && (
          <div className="text-center mt-4">
            <p className="text-sm text-muted-foreground font-mono">
              Page {currentPage + 1} of {totalPages} • {scenarios.length} total scenarios
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
